import React from 'react';
import { Globe, LeafyGreen, Squirrel, Pickaxe, Trees } from 'lucide-react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import ExploreButton from 'src/shared/components/explore-button/ExploreButton';
import PortfolioAreaChart from 'src/shared/components/chart-templates/portfolio-area-chart/PortfolioAreaReChart';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import HabitatMiniMap from 'src/app/maps/views/habitat-map/components/habitat-mini-map/HabitatMiniMap';
import { HabitatInsightsMetricType, habitatInsightsMetricTypeLabel } from 'src/shared/types/graph/survey';
import { landCoverMetricColourMap, getBiodiversityIntactnessClassification } from 'src/shared/helpers/metricHelpers';
import GaugeChart from 'src/shared/components/charts/GaugeChart';
import { SiteSummaryData } from '../state/api/siteSummaryQuery';
import useSiteSummaryAnalytics from '../hooks/useSiteSummaryAnalytics';
import styles from '../SiteSummary.module.scss';

type Props = {
    site: SiteSummaryData;
};

const SiteHabitatHealth = ({ site }: Props) => {
    const navigate = useAppNavigation();
    const { trackExploreButtonClick } = useSiteSummaryAnalytics();

    const getSeries = (label: string) => {
        const metric = site.metrics.find(m => m.label === label);
        if (!metric) return undefined;
        if ('datum' in metric && metric.datum && metric.years) {
            return metric.years.map((year, i) => ({ year, value: metric.datum?.[i] || 0 }));
        }
        return undefined;
    };

    const landCoverMetric = site.metrics.find(m => m.label === habitatInsightsMetricTypeLabel.Land_Cover);
    const vegetationSeries = getSeries(habitatInsightsMetricTypeLabel.Vegetation_Condition);
    const connectivitySeries = getSeries(habitatInsightsMetricTypeLabel.All_Connectivity);
    const humanModificationMetric = site.metrics.find(
        m => m.name === HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX || m.label === 'Human modification'
    );

    const biodiversityMetric = site.metrics.find(m => m.name === HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX);

    // Prepare land cover chart data (latest year snapshot + trend since first year)
    let landCoverChartData: { label: string; value: number; color: string; initialYear: number; latestYear: number; trendValue: number }[] =
        [];
    if (landCoverMetric && 'metricWithTrend' in landCoverMetric && landCoverMetric.metricWithTrend) {
        const trendData = landCoverMetric.metricWithTrend as unknown as Record<string, Record<string, number>>;
        const years = Object.keys(trendData).map(Number).sort();
        const initialYear = years[0];
        const latestYear = years[years.length - 1];
        const initialMetrics = trendData[initialYear];
        const latestMetrics = trendData[latestYear];

        landCoverChartData = landCoverMetricColourMap.map(entry => ({
            label: entry.label,
            value: latestMetrics[entry.metricKey || entry.label] || 0,
            color: entry.color,
            initialYear,
            latestYear,
            trendValue:
                (((latestMetrics[entry.metricKey || entry.label] || 0) - (initialMetrics[entry.metricKey || entry.label] || 0)) /
                    (initialMetrics[entry.metricKey || entry.label] || 1)) *
                100,
        }));
    }

    // Biodiversity Intactness classification
    const biodiversityValue = Number(biodiversityMetric?.data || 0);
    const biodiversityYear = biodiversityMetric?.year;
    const intactnessClassification = getBiodiversityIntactnessClassification(biodiversityValue);

    const handleExploreButtonClick = (section: string) => {
        trackExploreButtonClick({ section: section });
        navigate.toHabitatTrends();
    };

    return (
        <div className='grid grid-cols-2 gap-3'>
            <div className='col-span-2'>
                <div className={`text-xl text-black ${styles.sectionTitle}`}>Habitat health indicators</div>
            </div>

            {landCoverMetric && landCoverChartData.length > 0 && (
                <SummaryCard
                    containerClasses={`gap-3 p-5 h-[300px] ${styles.habitatCardLandCover}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <Globe className='text-primary' />
                        </div>
                    }
                    cardTitle='Land cover'
                    cardSubtitle='Distribution of land cover types across the site, highlighting dominant land cover and land uses.'
                    cardBody={
                        <div className='flex gap-5'>
                            <div className='flex gap-6'>
                                <div className='flex flex-col gap-3'>
                                    <div className='flex flex-col'>
                                        <span className='text-[42px] text-grey-80'>
                                            {landCoverChartData.find(e => e.label === 'Cumulative vegetation area')?.value || 0}%
                                        </span>
                                        <span className='flex items-center text-sm text-grey-80 pl-1'>Combined vegetated area</span>
                                    </div>
                                    <div className='flex gap-2 bg-grey-0 px-2 py-1 rounded-2xl text-xs items-center w-fit'>
                                        <div className='text-xs'>
                                            <span className='text-grey-80'>
                                                {(
                                                    landCoverChartData.find(e => e.label === 'Cumulative vegetation area')?.trendValue || 0
                                                ).toFixed(2)}
                                                %
                                            </span>
                                            <span className='text-grey-80'> since {landCoverChartData[0].initialYear}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className='flex flex-col gap-3'>
                                    <div className='flex flex-col'>
                                        <span className='text-[42px] text-grey-80'>
                                            {landCoverChartData.find(e => e.label === 'Trees')?.value || 0}%
                                        </span>
                                        <span className='flex items-center text-sm text-grey-80 pl-1'>Tree cover</span>
                                    </div>
                                    <div className='flex gap-2 bg-grey-0 px-2 py-1 rounded-2xl text-xs items-center w-fit'>
                                        <div className='text-xs'>
                                            <span className='text-grey-80'>
                                                {(landCoverChartData.find(e => e.label === 'Trees')?.trendValue || 0).toFixed(2)}%
                                            </span>
                                            <span className='text-grey-80'> since {landCoverChartData[0].initialYear}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    action={<ExploreButton sectionName='land-cover' onClick={() => handleExploreButtonClick('land_cover')} />}
                />
            )}

            <div className={`rounded-lg shadow-portfolio-summary-card overflow-hidden h-[300px] ${styles.mapCard}`}>
                <HabitatMiniMap />
            </div>

            {vegetationSeries && (
                <SummaryCard
                    containerClasses={`gap-3 p-5 h-[340px] ${styles.habitatCardVegetation}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <LeafyGreen className='text-primary' />
                        </div>
                    }
                    cardTitle='Vegetation health'
                    cardSubtitle='Assessment of plant vigor and density, crucial for ecosystem function.'
                    cardBody={<PortfolioAreaChart chartData={vegetationSeries} chartHeight={140} />}
                    action={<ExploreButton sectionName='vegetation-health' onClick={() => handleExploreButtonClick('vegetation_health')} />}
                />
            )}

            {connectivitySeries && (
                <SummaryCard
                    containerClasses={`gap-3 p-5 h-[340px] ${styles.habitatCardConnectivity}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <Trees className='text-primary' />
                        </div>
                    }
                    cardTitle='Connectivity'
                    cardSubtitle='Degree to which natural habitats are linked, allowing species movement.'
                    cardBody={<PortfolioAreaChart chartData={connectivitySeries} chartHeight={140} />}
                    action={<ExploreButton sectionName='connectivity' onClick={() => handleExploreButtonClick('connectivity')} />}
                />
            )}

            {biodiversityMetric && biodiversityYear && (
                <SummaryCard
                    containerClasses={`gap-3 p-5 ${styles.habitatCardBiodiversity}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <Squirrel className='text-primary' />
                        </div>
                    }
                    cardTitle={
                        <span className='flex flex-col items-start'>
                            Biodiversity Intactness Index
                            <span className='text-sm text-grey-80'>For {biodiversityYear}</span>
                        </span>
                    }
                    cardSubtitle='Measures the remaining biodiversity relative to an undisturbed baseline.'
                    cardBody={
                        <div className='flex justify-center'>
                            <GaugeChart
                                value={biodiversityValue}
                                startLabel={
                                    <>
                                        Severely
                                        <br />
                                        degraded
                                    </>
                                }
                                endLabel={'Intact'}
                                color={intactnessClassification.color}
                                category={intactnessClassification.category}
                            />
                        </div>
                    }
                    action={
                        <ExploreButton
                            sectionName='biodiversity-intactness'
                            onClick={() => handleExploreButtonClick('biodiversity_intactness')}
                        />
                    }
                />
            )}

            {humanModificationMetric && (
                <SummaryCard
                    containerClasses={`gap-3 p-5 ${styles.habitatCardHuman}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <Pickaxe className='text-primary' />
                        </div>
                    }
                    cardTitle='Human modification index'
                    cardSubtitle='Index summarising anthropogenic pressure.'
                    cardBody={(() => {
                        let latest = 0;
                        if ('data' in humanModificationMetric && humanModificationMetric.data) {
                            latest = Number(humanModificationMetric.data || 0);
                        } else if ('datum' in humanModificationMetric && humanModificationMetric.datum && humanModificationMetric.years) {
                            latest = humanModificationMetric.datum[humanModificationMetric.datum.length - 1] || 0;
                        }
                        let category = 'Low modification';
                        let color: 'green' | 'orange' | 'red' = 'green';
                        if (latest > 0.1 && latest <= 0.4) {
                            category = 'Moderate modification';
                            color = 'orange';
                        } else if (latest > 0.4) {
                            category = latest > 0.7 ? 'Very high modification' : 'High modification';
                            color = 'red';
                        }
                        return (
                            <div className='flex justify-center'>
                                <GaugeChart
                                    value={latest}
                                    startLabel={'Natural'}
                                    endLabel={
                                        <>
                                            Very high
                                            <br />
                                            modification
                                        </>
                                    }
                                    color={color}
                                    category={category}
                                />
                            </div>
                        );
                    })()}
                    action={
                        <ExploreButton sectionName='human-modification' onClick={() => handleExploreButtonClick('human_modification')} />
                    }
                />
            )}
        </div>
    );
};

export default SiteHabitatHealth;
